import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import { formatPaymentMethod } from '../../../utils/helper';

// Bootstrap badge class per approval state.
const approvalBadge = (status) => {
    if (status === 'Approved') return 'badge-light-success';
    if (status === 'Rejected') return 'badge-light-danger';
    return 'badge-light-warning';
};

const Subscribers = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const planFilter = searchParams.get('plan_id') || '';
    const approvalFilter = searchParams.get('approval_status') || '';

    const [isLoader, setIsLoader] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [processingAction, setProcessingAction] = useState(null);
    const [subscribers, setSubscribers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);

    useEffect(() => {
        getPlansList();
    }, []);

    useEffect(() => {
        getSubscribers();
    }, [page, planFilter, approvalFilter]);

    const getPlansList = async () => {
        try {
            const response = await ApiService.request({
                method: "GET",
                url: "admin/membership-plans-list",
            });
            if (response.data.status) setPlans(response.data.data);
        } catch (error) {
            console.error(error);
        }
    };

    const getSubscribers = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/membership-plans/subscribers",
                data: {
                    page,
                    ...(planFilter && { plan_id: planFilter }),
                    ...(approvalFilter && { approval_status: approvalFilter }),
                },
            });
            const data = response.data;
            if (data.status) {
                setSubscribers(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    // Merge one filter change into the URL params, keeping the other filter intact.
    const applyFilters = (next) => {
        setPage(1);
        const merged = { plan_id: planFilter, approval_status: approvalFilter, ...next };
        const params = {};
        if (merged.plan_id) params.plan_id = merged.plan_id;
        if (merged.approval_status) params.approval_status = merged.approval_status;
        setSearchParams(params);
    };

    const updateApproval = async (id, approval_status) => {
        const isApprove = approval_status === 'Approved';
        // A rejection has to say why — the member is told the reason, and it stays
        // on the record.
        const result = await Swal.fire({
            title: isApprove ? 'Are you sure?' : 'Reject Membership',
            text: isApprove
                ? 'This will charge the authorised payment and activate the membership.'
                : 'This will release the payment hold and reject the membership.',
            icon: 'warning',
            input: isApprove ? undefined : 'textarea',
            inputLabel: isApprove ? undefined : 'Reason for rejection',
            inputPlaceholder: isApprove ? undefined : 'This is shown to the member…',
            inputAttributes: isApprove ? undefined : { maxlength: 300 },
            inputValidator: isApprove
                ? undefined
                : (value) => (!value || !value.trim() ? 'A rejection reason is required' : undefined),
            showCancelButton: true,
            confirmButtonColor: isApprove ? '#4BC95F' : '#d33',
            confirmButtonText: isApprove ? 'Yes, approve' : 'Yes, reject',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            setProcessingId(id);
            setProcessingAction(approval_status);
            try {
                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/membership-plans/subscribers/${id}/approval`,
                    data: {
                        approval_status,
                        ...(!isApprove && { rejection_reason: result.value.trim() }),
                    },
                });
                const data = response.data;
                if (data.status) {
                    await getSubscribers();
                    toast.success(data.message);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error('Something went wrong.');
            } finally {
                setProcessingId(null);
                setProcessingAction(null);
            }
        }
    };

    // Pending approvals hold an authorised payment, so the server refuses to
    // delete them until they're approved or rejected.
    const deleteMembership = async (sub) => {
        const result = await Swal.fire({
            title: 'Delete this membership?',
            text: `This permanently removes ${sub.user_id?.name ? `${sub.user_id.name}'s` : 'this'} ${sub.plan_id?.title || 'membership'} record. No payment or refund is processed.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });
        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/membership-plans/subscribers/${sub._id}`,
            });
            const data = response.data;
            if (data.status) {
                await getSubscribers();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Something went wrong.');
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Subscribers</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/membership-plans" className="text-muted text-hover-primary">Membership Plans</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Subscribers</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md-4">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={planFilter}
                                            onChange={(e) => applyFilters({ plan_id: e.target.value })}
                                        >
                                            <option value="">All Plans</option>
                                            {plans.map((plan) => (
                                                <option key={plan._id} value={plan._id}>{plan.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={approvalFilter}
                                            onChange={(e) => applyFilters({ approval_status: e.target.value })}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Member</th>
                                                <th>Contact</th>
                                                <th>Plan</th>
                                                <th>Sessions</th>
                                                <th>Payment Method</th>
                                                <th>Subscribed On</th>
                                                <th>Approval</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {subscribers.length > 0 ? subscribers.map((sub) => (
                                                <tr key={sub._id}>
                                                    <td>
                                                        {sub.user_id ? (
                                                            <Link to={`/admin/users/${sub.user_id._id}`} className="text-dark fw-bold text-hover-primary">
                                                                {sub.user_id.name || '—'}
                                                            </Link>
                                                        ) : <span className="text-muted">Deleted user</span>}
                                                    </td>
                                                    <td>
                                                        <div>{sub.user_id?.email || '—'}</div>
                                                        <div className="text-muted fs-7">{sub.user_id?.phone || ''}</div>
                                                    </td>
                                                    <td>{sub.plan_id?.title || '—'}</td>
                                                    <td>
                                                        {sub.plan_id?.is_unlimited_sessions ? (
                                                            <span className="badge badge-light-info">Unlimited</span>
                                                        ) : (
                                                            <span>{sub.plan_id?.no_sessions ?? '—'}</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>{formatPaymentMethod(sub.payment_method)}</div>
                                                        {sub.transaction_id && (
                                                            <div className="text-muted fs-8 text-break" style={{ maxWidth: 200 }}>
                                                                {sub.transaction_id}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{sub.createdAt ? moment(sub.createdAt).format('DD MMM YYYY hh:mm A') : '—'}</td>
                                                    <td>
                                                        <span className={`badge ${approvalBadge(sub.approval_status)}`}>
                                                            {sub.approval_status || 'Approved'}
                                                        </span>
                                                        {sub.approval_status === 'Rejected' && sub.rejection_reason && (
                                                            <div className="text-muted fs-8 mt-1" style={{ maxWidth: 220 }}>
                                                                {sub.rejection_reason}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="mt-2">
                                                        {sub.approval_status === 'Pending' ? (
                                                            <>
                                                                <button
                                                                    onClick={() => updateApproval(sub._id, 'Approved')}
                                                                    className="btn btn-sm h-35px btn-light-success"
                                                                    title="Approve"
                                                                    disabled={processingId === sub._id}
                                                                >
                                                                    {processingId === sub._id && processingAction === 'Approved' ? (
                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    ) : (
                                                                        <i className="bi bi-check-lg fs-6"></i>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={() => updateApproval(sub._id, 'Rejected')}
                                                                    className="btn btn-sm h-35px btn-light-danger ms-2"
                                                                    title="Reject"
                                                                    disabled={processingId === sub._id}
                                                                >
                                                                    {processingId === sub._id && processingAction === 'Rejected' ? (
                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    ) : (
                                                                        <i className="bi bi-x-lg fs-6"></i>
                                                                    )}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() => deleteMembership(sub)}
                                                                className="btn btn-sm h-35px btn-light-danger"
                                                                title="Delete"
                                                            >
                                                                <i className="bi bi-trash fs-6"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="8" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={setPage}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Subscribers;
